import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ContentCopy as CopyIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { contextAPI } from '../../services/api/context';
import { AdaptationRule, RuleCondition, RuleAction } from '../../types/context';

interface RuleEditorProps {
  userId: string;
  organizationId: string;
}

interface SortableRuleProps {
  rule: AdaptationRule;
  onEdit: (rule: AdaptationRule) => void;
  onTest: (rule: AdaptationRule) => void;
  onDelete: (ruleId: string) => void;
}

const SortableRule: React.FC<SortableRuleProps> = ({ rule, onEdit, onTest, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 16,
    backgroundColor: isDragging ? 'action.hover' : 'background.paper',
  };

  return (
    <Card ref={setNodeRef} style={{ ...style, opacity: isDragging ? 0.5 : 1 }}>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div {...attributes} {...listeners} style={{ marginRight: 16, cursor: 'grab' }}>
            <DragIcon />
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="h6">{rule.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {rule.description}
            </Typography>
            <div style={{ marginTop: 8 }}>
              <Chip
                label={`Priority: ${rule.priority}`}
                size="small"
                style={{ marginRight: 8 }}
              />
              <Chip
                label={rule.enabled ? 'Enabled' : 'Disabled'}
                color={rule.enabled ? 'success' : 'default'}
                size="small"
                style={{ marginRight: 8 }}
              />
              <Chip
                label={`${rule.conditions.length} conditions`}
                size="small"
                style={{ marginRight: 8 }}
              />
              <Chip
                label={`${rule.actions.length} actions`}
                size="small"
              />
            </div>
          </div>
        </div>
      </CardContent>
      <CardActions>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => onEdit(rule)}
        >
          Edit
        </Button>
        <Button
          size="small"
          startIcon={<TestIcon />}
          onClick={() => onTest(rule)}
        >
          Test
        </Button>
        <Button
          size="small"
          startIcon={<CopyIcon />}
          onClick={() => onEdit({ ...rule, id: '' })}
        >
          Duplicate
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(rule.id)}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );
};

const RuleEditor: React.FC<RuleEditorProps> = ({ userId, organizationId }) => {
  const [rules, setRules] = useState<AdaptationRule[]>([]);
  const [editingRule, setEditingRule] = useState<AdaptationRule | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<any>(null);

  // @dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Available conditions and actions
  const conditionTypes = [
    { value: 'time_of_day', label: 'Time of Day' },
    { value: 'activity_type', label: 'Activity Type' },
    { value: 'focus_level', label: 'Focus Level' },
    { value: 'app_usage', label: 'Application Usage' },
    { value: 'context_pattern', label: 'Context Pattern' },
    { value: 'location', label: 'Location' },
    { value: 'device_type', label: 'Device Type' },
    { value: 'workload', label: 'Workload Level' },
  ];

  const actionTypes = [
    { value: 'adjust_ui', label: 'Adjust UI Layout' },
    { value: 'filter_notifications', label: 'Filter Notifications' },
    { value: 'suggest_break', label: 'Suggest Break' },
    { value: 'change_theme', label: 'Change Theme' },
    { value: 'prioritize_content', label: 'Prioritize Content' },
    { value: 'enable_focus_mode', label: 'Enable Focus Mode' },
    { value: 'adjust_recommendations', label: 'Adjust Recommendations' },
    { value: 'trigger_automation', label: 'Trigger Automation' },
  ];

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    setLoading(true);
    try {
      const response = await contextAPI.getAdaptationRules();
      setRules(response.data.rules);
    } catch (err: any) {
      setError('Failed to load adaptation rules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = () => {
    const newRule: AdaptationRule = {
      id: '',
      name: '',
      description: '',
      priority: 50,
      enabled: true,
      conditions: [],
      actions: [],
      metadata: {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setEditingRule(newRule);
    setIsDialogOpen(true);
  };

  const handleEditRule = (rule: AdaptationRule) => {
    setEditingRule({ ...rule });
    setIsDialogOpen(true);
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) {
      return;
    }

    try {
      await contextAPI.deleteAdaptationRule(ruleId);
      setSuccess('Rule deleted successfully');
      loadRules();
    } catch (err: any) {
      setError('Failed to delete rule');
    }
  };

  const handleSaveRule = async () => {
    if (!editingRule) return;

    try {
      if (editingRule.id) {
        await contextAPI.updateAdaptationRule(editingRule.id, editingRule);
        setSuccess('Rule updated successfully');
      } else {
        await contextAPI.createAdaptationRule(editingRule);
        setSuccess('Rule created successfully');
      }
      setIsDialogOpen(false);
      setEditingRule(null);
      loadRules();
    } catch (err: any) {
      setError('Failed to save rule');
    }
  };

  const handleTestRule = async (rule: AdaptationRule) => {
    try {
      const response = await contextAPI.testAdaptationRule(rule);
      setTestResults(response.data);
    } catch (err: any) {
      setError('Failed to test rule');
    }
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = rules.findIndex((rule) => rule.id === active.id);
    const newIndex = rules.findIndex((rule) => rule.id === over.id);

    const items = arrayMove(rules, oldIndex, newIndex);

    // Update priorities based on new order
    const updatedItems = items.map((item, index) => ({
      ...item,
      priority: 100 - index * 10,
    }));

    setRules(updatedItems);

    // Save updated priorities
    try {
      await Promise.all(
        updatedItems.map(rule =>
          contextAPI.updateAdaptationRule(rule.id, { priority: rule.priority })
        )
      );
      setSuccess('Rule priorities updated');
    } catch (err: any) {
      setError('Failed to update priorities');
      loadRules(); // Reload original order
    }
  };

  const addCondition = () => {
    if (!editingRule) return;
    
    const newCondition: RuleCondition = {
      type: 'time_of_day',
      operator: 'equals',
      value: '',
      parameters: {},
    };
    
    setEditingRule({
      ...editingRule,
      conditions: [...editingRule.conditions, newCondition],
    });
  };

  const addAction = () => {
    if (!editingRule) return;
    
    const newAction: RuleAction = {
      type: 'adjust_ui',
      parameters: {},
      priority: 50,
    };
    
    setEditingRule({
      ...editingRule,
      actions: [...editingRule.actions, newAction],
    });
  };

  const updateCondition = (index: number, condition: RuleCondition) => {
    if (!editingRule) return;
    
    const conditions = [...editingRule.conditions];
    conditions[index] = condition;
    
    setEditingRule({
      ...editingRule,
      conditions,
    });
  };

  const updateAction = (index: number, action: RuleAction) => {
    if (!editingRule) return;
    
    const actions = [...editingRule.actions];
    actions[index] = action;
    
    setEditingRule({
      ...editingRule,
      actions,
    });
  };

  const removeCondition = (index: number) => {
    if (!editingRule) return;
    
    setEditingRule({
      ...editingRule,
      conditions: editingRule.conditions.filter((_, i) => i !== index),
    });
  };

  const removeAction = (index: number) => {
    if (!editingRule) return;
    
    setEditingRule({
      ...editingRule,
      actions: editingRule.actions.filter((_, i) => i !== index),
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Typography variant="h4">Adaptation Rules</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateRule}
        >
          Create Rule
        </Button>
      </div>

      {loading ? (
        <Typography>Loading rules...</Typography>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={rules.map(rule => rule.id)} strategy={verticalListSortingStrategy}>
            <div>
              {rules.map((rule) => (
                <SortableRule
                  key={rule.id}
                  rule={rule}
                  onEdit={handleEditRule}
                  onTest={handleTestRule}
                  onDelete={handleDeleteRule}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Rule Editor Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingRule?.id ? 'Edit Rule' : 'Create Rule'}
        </DialogTitle>
        <DialogContent>
          {editingRule && (
            <div style={{ marginTop: 16 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Rule Name"
                    value={editingRule.name}
                    onChange={(e) =>
                      setEditingRule({ ...editingRule, name: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={editingRule.description}
                    onChange={(e) =>
                      setEditingRule({ ...editingRule, description: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography gutterBottom>Priority</Typography>
                  <Slider
                    value={editingRule.priority}
                    onChange={(_, value) =>
                      setEditingRule({ ...editingRule, priority: value as number })
                    }
                    valueLabelDisplay="auto"
                    step={10}
                    marks
                    min={0}
                    max={100}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingRule.enabled}
                        onChange={(e) =>
                          setEditingRule({ ...editingRule, enabled: e.target.checked })
                        }
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
              </Grid>

              <Divider style={{ marginTop: 24, marginBottom: 24 }} />

              {/* Conditions Section */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Typography variant="h6">Conditions</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>
                    Add Condition
                  </Button>
                </div>
                {editingRule.conditions.map((condition, index) => (
                  <Paper key={index} style={{ padding: 16, marginBottom: 8 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={condition.type}
                            onChange={(e) =>
                              updateCondition(index, { ...condition, type: e.target.value })
                            }
                            label="Type"
                          >
                            {conditionTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Operator</InputLabel>
                          <Select
                            value={condition.operator}
                            onChange={(e) =>
                              updateCondition(index, { ...condition, operator: e.target.value })
                            }
                            label="Operator"
                          >
                            <MenuItem value="equals">Equals</MenuItem>
                            <MenuItem value="not_equals">Not Equals</MenuItem>
                            <MenuItem value="contains">Contains</MenuItem>
                            <MenuItem value="greater_than">Greater Than</MenuItem>
                            <MenuItem value="less_than">Less Than</MenuItem>
                            <MenuItem value="between">Between</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Value"
                          value={condition.value}
                          onChange={(e) =>
                            updateCondition(index, { ...condition, value: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeCondition(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </div>

              {/* Actions Section */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Typography variant="h6">Actions</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addAction}>
                    Add Action
                  </Button>
                </div>
                {editingRule.actions.map((action, index) => (
                  <Paper key={index} style={{ padding: 16, marginBottom: 8 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Action</InputLabel>
                          <Select
                            value={action.type}
                            onChange={(e) =>
                              updateAction(index, { ...action, type: e.target.value })
                            }
                            label="Action"
                          >
                            {actionTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Parameters (JSON)"
                          value={JSON.stringify(action.parameters)}
                          onChange={(e) => {
                            try {
                              const params = JSON.parse(e.target.value);
                              updateAction(index, { ...action, parameters: params });
                            } catch (err) {
                              // Invalid JSON, ignore
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeAction(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveRule}
            disabled={!editingRule?.name || editingRule.conditions.length === 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Results Dialog */}
      {testResults && (
        <Dialog
          open={true}
          onClose={() => setTestResults(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Test Results</DialogTitle>
          <DialogContent>
            <Typography variant="body2" component="pre">
              {JSON.stringify(testResults, null, 2)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTestResults(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RuleEditor;